import {
  ChangeEvent,
  KeyboardEvent,
  ReactElement,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react'
import classNames from 'classnames'
import {useTranslation} from 'react-i18next'

import {HistoryIcon, PlusIconCorrect, SearchIcon} from 'components'
import {KEY_CODES} from 'definitions/constant'
import {DopamineSessionSummary} from 'interfaces'
import {DopamineAssistantSessionController} from './DopamineAssistant.types'
import {
  doesDopamineSessionMatchSearch,
  DopamineSessionDayGroup,
  formatDopamineSessionTime,
  groupDopamineSessionsByDay,
} from './DopamineAssistant.utils'
import {DopamineAssistantResizeHandle} from './DopamineAssistantResizeHandle'
import {DopamineHistorySidebarResize} from './useDopamineHistorySidebarWidth'

export const DOPAMINE_HISTORY_SIDEBAR_ID = 'dopamine-assistant-history-sidebar'

const DAY_GROUP_LABEL_KEYS: Record<DopamineSessionDayGroup, string> = {
  earlier: 'EARLIER',
  today: 'TODAY',
  yesterday: 'YESTERDAY',
}

export type DopamineAssistantHistoryControls = 'popover' | 'sidebar'

type DopamineAssistantHistoryProps = {
  /**
   * The surface the trigger controls: the popover this component renders
   * itself, or the sidebar the shell renders with
   * DopamineAssistantHistorySidebar in full screen.
   */
  controls?: DopamineAssistantHistoryControls
  isOpen: boolean
  onClose(): void
  onOpen(): void
  session: DopamineAssistantSessionController
}

type DopamineAssistantHistorySidebarProps = {
  isNewChatDisabled: boolean
  onNewChat(): void
  resize: DopamineHistorySidebarResize
  session: DopamineAssistantSessionController
}

type DopamineAssistantHistorySearchProps = {
  autoFocus?: boolean
  onChange(event: ChangeEvent<HTMLInputElement>): void
  onKeyDown?(event: KeyboardEvent<HTMLInputElement>): void
  value: string
}

type DopamineAssistantHistoryListProps = {
  currentSessionId?: string
  onSelect(item: DopamineSessionSummary): void
  search: string
  session: DopamineAssistantSessionController
  /** The sidebar gives rows a little more type size and breathing room. */
  variant?: DopamineAssistantHistoryControls
  visibleSessions: DopamineSessionSummary[]
}

type DopamineHistorySearchState = {
  handleSearch(event: ChangeEvent<HTMLInputElement>): void
  search: string
  setSearch(search: string): void
  visibleSessions: DopamineSessionSummary[]
}

/** Search text and the sessions it leaves visible; shared by popover and sidebar. */
const useDopamineHistorySearch = (
  session: DopamineAssistantSessionController,
): DopamineHistorySearchState => {
  const [search, setSearch] = useState('')
  const visibleSessions = useMemo(() => {
    const query = search.trim()
    return query
      ? session.sessions.filter(item =>
          doesDopamineSessionMatchSearch(item, query),
        )
      : session.sessions
  }, [search, session.sessions])

  const handleSearch = (event: ChangeEvent<HTMLInputElement>): void => {
    setSearch(event.target.value)
  }

  return {handleSearch, search, setSearch, visibleSessions}
}

const DopamineAssistantHistorySearch = ({
  autoFocus = false,
  onChange,
  onKeyDown,
  value,
}: DopamineAssistantHistorySearchProps): ReactElement => {
  const {t} = useTranslation(['common'], {useSuspense: false})

  return (
    <label className="dopamine-history-search">
      <SearchIcon
        classes=""
        height={14}
        strokeColor="currentColor"
        strokeWidth={1.8}
        width={14}
      />
      <input
        aria-label={t('DOPAMINE_SEARCH_PAST_CHATS')}
        autoFocus={autoFocus}
        className="min-w-0 flex-1 bg-transparent text-13px text-white outline-none placeholder:text-gray-600"
        placeholder={t('DOPAMINE_SEARCH_PAST_CHATS')}
        type="search"
        value={value}
        onChange={onChange}
        onKeyDown={onKeyDown}
      />
    </label>
  )
}

const DopamineAssistantHistoryList = ({
  currentSessionId,
  onSelect,
  search,
  session,
  variant = 'popover',
  visibleSessions,
}: DopamineAssistantHistoryListProps): ReactElement => {
  const {t} = useTranslation(['common'], {useSuspense: false})
  const groupLabelIdPrefix = useId()
  const isSidebar = variant === 'sidebar'
  const showEmptyState =
    !session.isSessionsLoading &&
    !session.isSessionsError &&
    visibleSessions.length === 0

  return (
    <div
      aria-busy={session.isSessionsLoading}
      className="dopamine-history-body"
    >
      {session.isSessionsLoading ? (
        <p className="px-2 py-4 text-center text-xs text-gray-500">
          {t('DOPAMINE_LOADING_PAST_CHATS')}
        </p>
      ) : null}
      {session.isSessionsError ? (
        <p className="px-2 py-4 text-center text-xs text-red-400" role="alert">
          {t('DOPAMINE_PAST_CHATS_ERROR')}
        </p>
      ) : null}
      {showEmptyState ? (
        <p className="px-2 py-4 text-center text-xs text-gray-500">
          {search.trim()
            ? t('DOPAMINE_NO_MATCHING_CHATS')
            : t('DOPAMINE_NO_PAST_CHATS')}
        </p>
      ) : null}
      {/* Chats sit under Today, Yesterday and Earlier so times can stay short. */}
      {groupDopamineSessionsByDay(visibleSessions).map(({group, sessions}) => (
        <div
          aria-labelledby={`${groupLabelIdPrefix}-${group}`}
          className={classNames(
            isSidebar ? 'mt-3 space-y-0.5 first:mt-1' : 'mt-2.5 first:mt-1.5',
          )}
          key={group}
          role="group"
        >
          <p
            className="mx-2 mb-1 text-11px uppercase tracking-widest text-dopamine-muted"
            id={`${groupLabelIdPrefix}-${group}`}
          >
            {t(DAY_GROUP_LABEL_KEYS[group])}
          </p>
          {sessions.map(item => (
            <button
              aria-current={
                item.sessionId === currentSessionId ? 'true' : undefined
              }
              aria-label={item.title}
              className="dopamine-history-row"
              data-cy={`dopamine-assistant-session-${item.sessionId}`}
              key={item.sessionId}
              type="button"
              onClick={() => onSelect(item)}
            >
              <span className="min-w-0 flex-1">
                <span
                  className={classNames(
                    'block truncate text-white/90',
                    isSidebar ? 'text-13px leading-4' : 'text-xs',
                  )}
                >
                  {item.title}
                </span>
                <span
                  className={classNames(
                    'block truncate text-11px text-dopamine-muted',
                    isSidebar ? 'mt-1 tabular-nums' : 'mt-0.5',
                  )}
                >
                  {formatDopamineSessionTime(item.createdAt, group)}
                </span>
              </span>
            </button>
          ))}
        </div>
      ))}
    </div>
  )
}

/**
 * Past chats as a persistent sidebar. The shell renders it in full screen on
 * viewports wide enough to keep it beside the reading column.
 */
export const DopamineAssistantHistorySidebar = ({
  isNewChatDisabled,
  onNewChat,
  resize,
  session,
}: DopamineAssistantHistorySidebarProps): ReactElement => {
  const {t} = useTranslation(['common'], {useSuspense: false})
  const {handleSearch, search, setSearch, visibleSessions} =
    useDopamineHistorySearch(session)
  const handleSearchKeyDown = (
    event: KeyboardEvent<HTMLInputElement>,
  ): void => {
    // Escape clears a query first. With nothing to clear it reaches the
    // shell, which treats it as leaving full screen.
    if (event.key !== KEY_CODES.ESCAPE || !search) return

    event.preventDefault()
    event.stopPropagation()
    setSearch('')
  }

  return (
    <nav
      aria-label={t('DOPAMINE_PAST_CHATS')}
      className="dopamine-history-sidebar relative flex w-dopamine-history-sidebar flex-none flex-col border-r-0.5 border-white/10 bg-black/20"
      data-cy="dopamine-assistant-history-sidebar"
      id={DOPAMINE_HISTORY_SIDEBAR_ID}
      style={resize.width === null ? undefined : {width: resize.width}}
    >
      <header className="dopamine-history-header">
        {/* New chat lives where the list is, not a header-width away. */}
        <button
          className="mb-2.5 flex h-8 w-full items-center justify-center gap-1.5 rounded-ds-md border-0.5 border-dopamine-accent/40 bg-dopamine-accent/10 text-13px text-dopamine-accent transition-colors duration-base motion-reduce:transition-none hover:bg-dopamine-accent/20 focus:outline-none focus-visible:ring-1 focus-visible:ring-dopamine-accent disabled:cursor-default disabled:opacity-40 disabled:hover:bg-dopamine-accent/10"
          data-cy="dopamine-assistant-sidebar-new-chat"
          disabled={isNewChatDisabled}
          type="button"
          onClick={onNewChat}
        >
          <PlusIconCorrect
            classes="h-3.5 w-3.5"
            height={14}
            strokeColor="currentColor"
            width={14}
          />
          <span>{t('DOPAMINE_NEW_CHAT')}</span>
        </button>
        <p className="mx-0.5 mb-2 text-11px uppercase tracking-widest text-dopamine-muted">
          {t('DOPAMINE_PAST_CHATS')}
        </p>
        <DopamineAssistantHistorySearch
          value={search}
          onChange={handleSearch}
          onKeyDown={handleSearchKeyDown}
        />
      </header>
      <DopamineAssistantHistoryList
        currentSessionId={session.clientSessionId}
        search={search}
        session={session}
        variant="sidebar"
        visibleSessions={visibleSessions}
        onSelect={session.selectSession}
      />
      <DopamineAssistantResizeHandle
        ariaControls={DOPAMINE_HISTORY_SIDEBAR_ID}
        className="dopamine-history-resizer absolute inset-y-0 -right-1 z-10 w-2"
        dataCy="dopamine-assistant-history-resizer"
        label={t('DOPAMINE_RESIZE_PAST_CHATS')}
        resize={resize}
      />
    </nav>
  )
}

export const DopamineAssistantHistory = ({
  controls = 'popover',
  isOpen,
  onClose,
  onOpen,
  session,
}: DopamineAssistantHistoryProps): ReactElement => {
  const {t} = useTranslation(['common'], {useSuspense: false})
  const {handleSearch, search, setSearch, visibleSessions} =
    useDopamineHistorySearch(session)
  const dialogRef = useRef<HTMLDialogElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const isPopover = controls === 'popover'
  const isPopoverOpen = isPopover && isOpen

  const selectSession = (item: DopamineSessionSummary): void => {
    session.selectSession(item)
    onClose()
    triggerRef.current?.focus()
  }

  const handleDialogKeyDown = (event: KeyboardEvent<HTMLElement>): void => {
    if (event.key !== KEY_CODES.ESCAPE) return

    event.preventDefault()
    event.stopPropagation()
    onClose()
    triggerRef.current?.focus()
  }

  useEffect(() => {
    if (!isPopoverOpen) setSearch('')
  }, [isPopoverOpen])

  useEffect(() => {
    if (!isPopoverOpen) return

    const handleOutsidePointerDown = (event: PointerEvent): void => {
      const target = event.target
      if (!(target instanceof Node)) return
      if (
        dialogRef.current?.contains(target) ||
        triggerRef.current?.contains(target)
      ) {
        return
      }

      onClose()
      window.setTimeout(() => {
        const activeElement = document.activeElement
        if (
          activeElement === null ||
          activeElement === document.body ||
          !activeElement.isConnected
        ) {
          triggerRef.current?.focus()
        }
      }, 0)
    }

    document.addEventListener('pointerdown', handleOutsidePointerDown)
    return () =>
      document.removeEventListener('pointerdown', handleOutsidePointerDown)
  }, [isPopoverOpen, onClose])

  return (
    <>
      <button
        aria-controls={
          !isPopover && isOpen ? DOPAMINE_HISTORY_SIDEBAR_ID : undefined
        }
        aria-expanded={isOpen}
        aria-haspopup={isPopover ? 'dialog' : undefined}
        aria-label={t('DOPAMINE_PAST_CHATS')}
        className="dopamine-header-icon-button dopamine-tip"
        data-cy="dopamine-assistant-past-chats"
        data-tip={t('DOPAMINE_PAST_CHATS')}
        ref={triggerRef}
        type="button"
        onClick={isOpen ? onClose : onOpen}
      >
        <HistoryIcon height={16} strokeWidth={1.7} width={16} />
      </button>
      {isPopoverOpen ? (
        <dialog
          aria-label={t('DOPAMINE_PAST_CHATS')}
          className="dopamine-history-popover"
          ref={dialogRef}
          open
          onKeyDown={handleDialogKeyDown}
        >
          <header className="dopamine-history-header">
            <p className="mx-0.5 mb-2 text-11px uppercase tracking-widest text-dopamine-muted">
              {t('DOPAMINE_PAST_CHATS')}
            </p>
            <DopamineAssistantHistorySearch
              autoFocus
              value={search}
              onChange={handleSearch}
            />
          </header>
          <DopamineAssistantHistoryList
            currentSessionId={session.clientSessionId}
            search={search}
            session={session}
            visibleSessions={visibleSessions}
            onSelect={selectSession}
          />
        </dialog>
      ) : null}
    </>
  )
}
